import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { FarmCard } from '../../components/orchard';
import { TreePine, Plus, Loader2, Eye, EyeOff } from 'lucide-react';
import orchardService from '../../services/orchard';
import type { Farm, Pagination } from '../../types/orchard';

function OrchardDashboard() {
  const navigate = useNavigate();
  const [farms, setFarms] = useState<Farm[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showInactive, setShowInactive] = useState(false);

  const loadFarms = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await orchardService.getFarms(currentPage, 12, undefined, showInactive);
      if (response.success && response.data) {
        const data = response.data as { farms: Farm[]; pagination: Pagination };
        setFarms(data.farms);
        setPagination(data.pagination);
      } else {
        setError('Failed to load farms');
      }
    } catch {
      setError('Failed to load farms. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, showInactive]);

  useEffect(() => {
    loadFarms();
  }, [loadFarms]);

  const totalHectares = farms.reduce((sum, f) => sum + (Number(f.total_area_hectares) || 0), 0);
  const totalBlocks = farms.reduce((sum, f) => sum + (Number(f.block_count) || 0), 0);
  const totalAreas = farms.reduce((sum, f) => sum + (Number(f.cultivar_area_count) || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Farms"
        subtitle="Manage your farms, blocks, and cultivar plantings"
        icon={<TreePine className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => { setCurrentPage(1); setShowInactive((v) => !v); }}
              title={showInactive ? 'Hide inactive farms' : 'Show inactive farms'}
            >
              {showInactive ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              {showInactive ? 'Hide Inactive' : 'Show Inactive'}
            </Button>
            <Button variant="primary" onClick={() => navigate('/orchard/farms/new')}>
              <Plus className="h-4 w-4" />
              New Farm
            </Button>
          </div>
        }
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary Stats */}
        {!loading && farms.length > 0 && (
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-white rounded-xl border border-neutral-200 p-5 text-center shadow-sm">
              <p className="text-3xl font-bold text-neutral-900">{totalHectares.toFixed(1)}</p>
              <p className="text-sm text-neutral-500 mt-1">Total Hectares</p>
            </div>
            <div className="bg-white rounded-xl border border-neutral-200 p-5 text-center shadow-sm">
              <p className="text-3xl font-bold text-primary-600">{totalBlocks}</p>
              <p className="text-sm text-neutral-500 mt-1">Total Blocks</p>
            </div>
            <div className="bg-white rounded-xl border border-neutral-200 p-5 text-center shadow-sm">
              <p className="text-3xl font-bold text-success-600">{totalAreas}</p>
              <p className="text-sm text-neutral-500 mt-1">Cultivar Areas</p>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <Loader2 className="h-10 w-10 text-primary-600 animate-spin mx-auto mb-4" />
              <p className="text-neutral-600">Loading farms...</p>
            </div>
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="bg-error-50 border border-error-200 rounded-xl p-6 text-center">
            <p className="text-error-700 mb-4">{error}</p>
            <Button variant="outline" onClick={loadFarms}>Try Again</Button>
          </div>
        )}

        {/* Empty */}
        {!loading && !error && farms.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-12 text-center">
            <div className="h-16 w-16 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-4">
              <TreePine className="h-8 w-8 text-neutral-400" />
            </div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">No Farms Yet</h3>
            <p className="text-neutral-600 mb-6 max-w-md mx-auto">
              Set up your first farm by drawing its boundary on a map.
            </p>
            <Button variant="primary" onClick={() => navigate('/orchard/farms/new')}>
              <Plus className="h-4 w-4" />
              Create First Farm
            </Button>
          </div>
        )}

        {/* Farm Grid */}
        {!loading && !error && farms.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {farms.map((farm) => (
                <FarmCard key={farm.id} farm={farm} />
              ))}
            </div>

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                >
                  Previous
                </Button>
                <span className="text-sm text-neutral-600">
                  Page {currentPage} of {pagination.pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === pagination.pages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default OrchardDashboard;
